import PlayerScoreTable from "../../players/tables/PlayerScoreTable";
import MatchBox from "../../matches/MatchBox";
import {ROUND_STATES} from "../../../util/Constants";
import {isMatchFinished, sortMatches} from "../../../util/HelperUtils";
import {getLoggedUser} from "../../../util/SecurityUtils";
import {useTranslation} from "react-i18next";
import {InputGroup, Tab, Tabs} from "react-bootstrap";
import Form from "react-bootstrap/Form";
import {useState} from "react";
import {useIsMobile} from "../../../hooks/useIsMobile";
import type {Match, PlayerWithSeeding, TournamentRound, PlayerScore} from "../../../types/domain";

interface GroupsRoundTabProps {
  round: TournamentRound;
  onMatchClick: (match: Match) => void;
  players: PlayerWithSeeding[];
  tournamentId: number;
  canManage: boolean;
}

interface GroupData {
  group: number | null | undefined;
  playerIds: number[];
  scores: PlayerScore[];
  matches: Match[];
}

function GroupsRoundTab(props: GroupsRoundTabProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [ matchFilter, setMatchFilter ] = useState('')

  const isInProgress = (round: TournamentRound) => {
    return round.state === ROUND_STATES.IN_PROGRESS
  }

  const round = props.round

  const playerSecondGroupMap = new Map(
    props.players.map(player => [player.player.id, player.secondRoundGroup])
  )
  round.scores.forEach(score => {
    if (playerSecondGroupMap.has(score.player.id)) {
      score.player.secondRoundGroup = playerSecondGroupMap.get(score.player.id)
    }
  })

  const groupOptions = [...new Set(props.players.map(player =>
    round.roundOrder === 1 ? player.firstRoundGroup : player.secondRoundGroup
  ))].sort()
  const groups: GroupData[] = groupOptions.map(group => {
    const playerIds = props.players
      .filter(player => (round.roundOrder === 1 ? player.firstRoundGroup : player.secondRoundGroup) === group)
      .map(p => p.player.id);
    return {
      group,
      playerIds,
      scores: round.scores.filter(score => playerIds.includes(score.player.id)),
      matches: round.matches.filter(match => playerIds.includes(match.playerOne.id) || playerIds.includes(match.playerTwo.id)),
    };
  });

  const displayGroupColumn = props.canManage && (round.roundOrder === 1) && round.state === ROUND_STATES.IN_PROGRESS

  const loggedUser = getLoggedUser()
  let defaultActiveTab = "group-tab-1"
  if (loggedUser) {
    groups.forEach((groupData) => {
        if (groupData.playerIds.includes(loggedUser.playerId ?? -1)) {
          defaultActiveTab = "group-tab-" + groupData.group
        }
      }
    )
  }

  return (
    <Tabs
      id="group-system-tabs"
      defaultActiveKey={defaultActiveTab}
      variant="tabs"
      className="theme-tabs"
      style={{ borderColor: 'var(--border-color)' }}
    >
      { isMobile ? <Tab eventKey={"disabled-group-tab"} title={t("group") + ":"} disabled={true} /> : null }
      {groups.map(groupData => {
        const sortedMatches = sortMatches(groupData.matches, matchFilter)
        const playedMatches = sortedMatches.filter((match: Match) => isMatchFinished(match.state));
        const unfinishedMatches = sortedMatches.filter((match: Match) => !isMatchFinished(match.state));
        const tabTitle = isMobile ? groupData.group : t("groupTab", {groupNumber: groupData.group})

        const searchInput = (
          <InputGroup className="mt-3 me-5 mb-1">
            <InputGroup.Text id="searchbar" style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>{t("search")}</InputGroup.Text>
            <Form.Control onChange={e => setMatchFilter(e.target.value)} style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }} />
          </InputGroup>
        );

        const scoreTable = (
          <PlayerScoreTable scores={groupData.scores} displayGroups={displayGroupColumn} tournamentId={props.tournamentId} hideBuchholz={true} />
        );

        const matchesSection = isMobile ? (
          <>
            {searchInput}
            {sortedMatches.map(match => (
              <MatchBox key={"match-box-" + match.id} match={match} onClick={() => props.onMatchClick(match)} indicateFinished={isInProgress(round)} canManage={props.canManage} />
            ))}
          </>
        ) : (
          <>
            {searchInput}
            <div className="unplayed-match-in-row">
              {unfinishedMatches.map(match => (
                <MatchBox variant="unfinished" key={"match-box-" + match.id} match={match} onClick={() => props.onMatchClick(match)} canManage={props.canManage} />
              ))}
            </div>
            {playedMatches.map(match => (
              <MatchBox key={"match-box-" + match.id} match={match} onClick={() => props.onMatchClick(match)} indicateFinished={isInProgress(round)} canManage={props.canManage} />
            ))}
          </>
        );

        return (
          <Tab eventKey={"group-tab-" + groupData.group} title={tabTitle} key={"key-group-tab-" + groupData.group}>
            <div>
              {isMobile ? (
                <Tabs
                  defaultActiveKey={isInProgress(round) ? "matches-tab" : "score-tab"}
                  id="mobile-group-tabs"
                  variant="pills"
                  fill
                  className="theme-tabs"
                  style={{ borderColor: 'var(--border-color)' }}
                >
                  <Tab eventKey="score-tab" title={t("scoretable")}>{scoreTable}</Tab>
                  <Tab eventKey="matches-tab" title={t("matches")}>{matchesSection}</Tab>
                </Tabs>
              ) : (
                <>
                  <div className="left-half-responsive">{scoreTable}</div>
                  <div className="right-half-responsive">{matchesSection}</div>
                </>
              )}
            </div>
        </Tab>)
      })}
    </Tabs>
  )
}

export default GroupsRoundTab
